import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MediaInput } from "@/components/bi/MediaInput";
import { dimOutName } from "@/lib/pivotEngine";
import { Square, MessageSquare, ListOrdered, Plus, X, EyeOff, Eye, Hash, ChevronUp, ChevronDown } from "lucide-react";

function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-2 py-0.5">
      <Label className="text-[11px] text-[color:var(--ink-3)] shrink-0">{label}</Label>
      {children}
    </div>
  );
}

function ColorField({ value, fallback, onChange, testid }) {
  return (
    <div className="flex items-center gap-1">
      <input data-testid={testid} type="color" value={value || fallback} onChange={(e) => onChange(e.target.value)}
        className="h-7 w-9 rounded-lg border border-[color:var(--glass-border)] bg-transparent cursor-pointer" />
      {value && <button onClick={() => onChange("")} className="text-[color:var(--ink-3)] hover:text-[color:var(--ink)]"><X className="w-3 h-3" /></button>}
    </div>
  );
}
export { Row, ColorField };

// ---------- Item 57: widget appearance (bg, border, radius, padding, shadow) ----------
export function AppearanceEditor({ opts, patchOpts }) {
  const ap = opts.appearance || {};
  const set = (patch) => patchOpts({ appearance: { ...ap, ...patch } });
  return (
    <div data-testid="appearance-editor" className="space-y-1.5 rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-3">
      <Label className="text-xs text-[color:var(--ink-2)] flex items-center gap-1.5"><Square className="w-3.5 h-3.5" />Widget appearance</Label>
      <Row label="Transparent background"><Switch data-testid="ap-transparent" checked={!!ap.transparent} onCheckedChange={(v) => set({ transparent: v })} /></Row>
      <Row label="Background color"><ColorField testid="ap-bg-color" value={ap.bg_color} fallback="#ffffff" onChange={(v) => set({ bg_color: v })} /></Row>
      {ap.bg_color && (
        <div>
          <Label className="text-[11px] text-[color:var(--ink-3)]">Background opacity ({Math.round((ap.bg_opacity ?? 1) * 100)}%)</Label>
          <Slider className="mt-1.5" min={10} max={100} step={5} value={[Math.round((ap.bg_opacity ?? 1) * 100)]} onValueChange={([v]) => set({ bg_opacity: v / 100 })} />
        </div>
      )}
      <div>
        <Label className="text-[11px] text-[color:var(--ink-3)]">Background image</Label>
        <div className="mt-1"><MediaInput testid="ap-bg-image" value={ap.bg_image || ""} onChange={(v) => set({ bg_image: v })} /></div>
        {ap.bg_image && (
          <div className="mt-1.5">
            <Label className="text-[11px] text-[color:var(--ink-3)]">Image opacity ({Math.round((ap.bg_image_opacity ?? 0.25) * 100)}%)</Label>
            <Slider className="mt-1.5" min={5} max={100} step={5} value={[Math.round((ap.bg_image_opacity ?? 0.25) * 100)]} onValueChange={([v]) => set({ bg_image_opacity: v / 100 })} />
          </div>
        )}
      </div>
      <Row label="Border color"><ColorField testid="ap-border-color" value={ap.border_color} fallback="#d7deeb" onChange={(v) => set({ border_color: v })} /></Row>
      <div>
        <Label className="text-[11px] text-[color:var(--ink-3)]">Border width ({ap.border_width ?? 1}px)</Label>
        <Slider data-testid="ap-border-width" className="mt-1.5" min={0} max={6} step={1} value={[ap.border_width ?? 1]} onValueChange={([v]) => set({ border_width: v })} />
      </div>
      <div>
        <Label className="text-[11px] text-[color:var(--ink-3)]">Corner radius ({ap.radius ?? 16}px)</Label>
        <Slider data-testid="ap-radius" className="mt-1.5" min={0} max={32} step={2} value={[ap.radius ?? 16]} onValueChange={([v]) => set({ radius: v })} />
      </div>
      <div>
        <Label className="text-[11px] text-[color:var(--ink-3)]">Content padding ({ap.padding ?? 12}px)</Label>
        <Slider data-testid="ap-padding" className="mt-1.5" min={0} max={32} step={2} value={[ap.padding ?? 12]} onValueChange={([v]) => set({ padding: v })} />
      </div>
      <Row label="Shadow">
        <Select value={ap.shadow || "default"} onValueChange={(v) => set({ shadow: v })}>
          <SelectTrigger data-testid="ap-shadow" className="bg-[color:var(--surface)] w-[110px] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="default">Theme</SelectItem>
            <SelectItem value="none">None</SelectItem>
            <SelectItem value="sm">Soft</SelectItem>
            <SelectItem value="md">Medium</SelectItem>
            <SelectItem value="lg">Strong</SelectItem>
          </SelectContent>
        </Select>
      </Row>
      {(ap.bg_color || ap.bg_image || ap.transparent || ap.border_color || ap.shadow) && (
        <Button data-testid="ap-reset" size="sm" variant="ghost" className="h-6 text-[color:var(--ink-3)] w-full" onClick={() => patchOpts({ appearance: {} })}>Reset appearance</Button>
      )}
    </div>
  );
}

// ---------- Item 58: legend controls ----------
export function LegendEditor({ opts, patchOpts }) {
  const overrides = opts.seriesOverrides || [];
  const setOv = (arr) => patchOpts({ seriesOverrides: arr });
  return (
    <div data-testid="legend-editor" className="space-y-1.5 rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-3">
      <Label className="text-xs text-[color:var(--ink-2)] flex items-center gap-1.5"><ListOrdered className="w-3.5 h-3.5" />Legend</Label>
      <Row label="Position">
        <Select value={opts.legendPos || "bottom"} onValueChange={(v) => patchOpts({ legendPos: v })}>
          <SelectTrigger data-testid="legend-pos" className="bg-[color:var(--surface)] w-[100px] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="bottom">Bottom</SelectItem>
            <SelectItem value="top">Top</SelectItem>
            <SelectItem value="left">Left</SelectItem>
            <SelectItem value="right">Right</SelectItem>
          </SelectContent>
        </Select>
      </Row>
      <Row label={`Font size (${opts.legendFontSize || 12}px)`}>
        <div className="w-[110px]"><Slider data-testid="legend-fontsize" min={9} max={18} step={1} value={[opts.legendFontSize || 12]} onValueChange={([v]) => patchOpts({ legendFontSize: v })} /></div>
      </Row>
      <Row label="Font color"><ColorField testid="legend-color" value={opts.legendColor} fallback="#51607A" onChange={(v) => patchOpts({ legendColor: v })} /></Row>
      <Row label="Marker shape">
        <Select value={opts.legendIcon || "auto"} onValueChange={(v) => patchOpts({ legendIcon: v })}>
          <SelectTrigger data-testid="legend-icon" className="bg-[color:var(--surface)] w-[110px] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="auto">Auto</SelectItem>
            <SelectItem value="circle">Circle</SelectItem>
            <SelectItem value="rect">Square</SelectItem>
            <SelectItem value="roundRect">Rounded</SelectItem>
            <SelectItem value="triangle">Triangle</SelectItem>
            <SelectItem value="diamond">Diamond</SelectItem>
          </SelectContent>
        </Select>
      </Row>
      <div className="flex items-center justify-between pt-1">
        <Label className="text-[11px] text-[color:var(--ink-3)]">Series labels & visibility</Label>
        <Button data-testid="legend-add-override" size="sm" variant="ghost" className="h-6 text-[color:var(--brand-ocean)]" onClick={() => setOv([...overrides, { name: "", label: "", hidden: false }])}><Plus className="w-3.5 h-3.5" /></Button>
      </div>
      {overrides.map((s, i) => (
        <div key={i} className="flex gap-1 items-center">
          <Input data-testid={`legend-ov-name-${i}`} value={s.name} placeholder="Series name" onChange={(e) => setOv(overrides.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} className="bg-[color:var(--surface)] flex-1 min-w-0 h-7 text-xs" />
          <Input data-testid={`legend-ov-label-${i}`} value={s.label} placeholder="Custom label" onChange={(e) => setOv(overrides.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} className="bg-[color:var(--surface)] flex-1 min-w-0 h-7 text-xs" />
          <button data-testid={`legend-ov-hide-${i}`} title={s.hidden ? "Hidden — click to show" : "Visible — click to hide"} onClick={() => setOv(overrides.map((x, j) => j === i ? { ...x, hidden: !x.hidden } : x))} className="text-[color:var(--ink-3)] hover:text-[color:var(--ink)] p-0.5 shrink-0">
            {s.hidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          </button>
          <button onClick={() => setOv(overrides.filter((_, j) => j !== i))} className="text-[color:var(--ink-3)] hover:text-[color:var(--danger)] p-0.5 shrink-0"><X className="w-3.5 h-3.5" /></button>
        </div>
      ))}
      {overrides.length > 0 && <p className="text-[10px] text-[color:var(--ink-3)]">Use the exact series name shown in the legend, then give it a custom label or hide it.</p>}
    </div>
  );
}

// ---------- Item 55: tooltip framework ----------
const TT_AGGS = ["sum", "avg", "min", "max", "count", "count_distinct", "median"];
// Dates/text can't be summed or averaged — offer only order/count aggregations.
const TT_AGGS_NON_NUMERIC = ["min", "max", "count", "count_distinct"];
const NUMERIC_TYPES = ["number", "integer", "float", "int", "decimal", "numeric"];
export function TooltipEditor({ config, opts, patchOpts, patchConfig, fields, disp, alwaysExtras = false }) {
  const tt = opts.tooltip || {};
  const set = (patch) => patchOpts({ tooltip: { ...tt, ...patch } });
  const extras = config.tooltip_measures || [];
  const setExtras = (arr) => patchConfig({ tooltip_measures: arr });
  // Item 28: pick the field / series to rename from the widget's own fields (no typing the key by hand)
  const ttNames = React.useMemo(() => {
    const out = [];
    const push = (key, label) => { if (key && !out.some((o) => o.key === key)) out.push({ key, label: label || key }); };
    (config.dimensions || []).forEach((d) => push(dimOutName(d), d.label || dimOutName(d)));
    (config.measures || []).forEach((m) => push(m.key || m.alias || (m.field ? `${m.agg || "sum"}_${m.field}` : null), m.label || (disp && m.field ? disp(m.field) : m.field)));
    (config.tooltip_measures || []).forEach((t) => push(t.field ? `${t.agg || "sum"}_${t.field}` : null, t.label || t.field));
    ((opts.seriesOverrides) || []).forEach((s) => push(s.name, s.label || s.name));
    return out;
  }, [config.dimensions, config.measures, config.tooltip_measures, opts.seriesOverrides, disp]);
  return (
    <div data-testid="tooltip-editor" className="space-y-1.5 rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-3">
      <div className="flex items-center justify-between">
        <Label className="text-xs text-[color:var(--ink-2)] flex items-center gap-1.5"><MessageSquare className="w-3.5 h-3.5" />Rich tooltip</Label>
        <Switch data-testid="tooltip-custom" checked={!!tt.custom} onCheckedChange={(v) => set({ custom: v })} />
      </div>
      {(tt.custom || alwaysExtras) && (
        <>
          <div>
            <Label className="text-[11px] text-[color:var(--ink-3)]">Tooltip title (optional)</Label>
            <Input data-testid="tooltip-header" value={tt.header || ""} onChange={(e) => set({ header: e.target.value })} placeholder="Defaults to the hovered category" className="mt-1 bg-[color:var(--surface)] h-7 text-xs" />
          </div>
          <Row label="Show % share of hovered total"><Switch data-testid="tooltip-share" checked={!!tt.share} onCheckedChange={(v) => set({ share: v })} /></Row>
          <div className="flex items-center justify-between pt-1">
            <Label className="text-[11px] text-[color:var(--ink-3)]">Rename tooltip fields</Label>
            <Button data-testid="tooltip-add-rename" size="sm" variant="ghost" className="h-6 text-[color:var(--brand-ocean)]"
              onClick={() => set({ labels: [...(tt.labels || []), { name: "", label: "" }] })}><Plus className="w-3.5 h-3.5" /></Button>
          </div>
          {(tt.labels || []).map((l, i) => (
            <div key={i} className="flex gap-1 items-center">
              <Select value={l.name || ""} onValueChange={(v) => set({ labels: (tt.labels || []).map((x, j) => j === i ? { ...x, name: v } : x) })}>
                <SelectTrigger data-testid={`tooltip-rename-name-${i}`} className="bg-[color:var(--surface)] flex-1 min-w-0 h-7 text-xs"><SelectValue placeholder="Select field" /></SelectTrigger>
                <SelectContent>
                  {ttNames.map((n) => <SelectItem key={n.key} value={n.key}>{n.label}</SelectItem>)}
                  {l.name && !ttNames.some((n) => n.key === l.name) && <SelectItem value={l.name}>{l.name}</SelectItem>}
                </SelectContent>
              </Select>
              <Input data-testid={`tooltip-rename-label-${i}`} value={l.label || ""} placeholder="Show as"
                onChange={(e) => set({ labels: (tt.labels || []).map((x, j) => j === i ? { ...x, label: e.target.value } : x) })}
                className="bg-[color:var(--surface)] flex-1 min-w-0 h-7 text-xs" />
              <button onClick={() => set({ labels: (tt.labels || []).filter((_, j) => j !== i) })} className="text-[color:var(--ink-3)] hover:text-[color:var(--danger)] p-0.5 shrink-0"><X className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          <div className="flex items-center justify-between pt-1">
            <Label className="text-[11px] text-[color:var(--ink-3)]">Extra tooltip metrics</Label>
            <Button data-testid="tooltip-add-metric" size="sm" variant="ghost" className="h-6 text-[color:var(--brand-ocean)]" onClick={() => setExtras([...extras, { field: fields[0]?.name || "", agg: "sum" }])}><Plus className="w-3.5 h-3.5" /></Button>
          </div>
          {extras.map((t, i) => {
            const ftype = (fields.find((c) => c.name === t.field) || {}).type;
            const numeric = NUMERIC_TYPES.includes(ftype);
            const aggs = numeric ? TT_AGGS : TT_AGGS_NON_NUMERIC;
            return (
            <div key={i} className="space-y-1">
            <div className="flex gap-1 items-center">
              <Select value={t.field || ""} onValueChange={(v) => {
                const vt = (fields.find((c) => c.name === v) || {}).type;
                const vAggs = NUMERIC_TYPES.includes(vt) ? TT_AGGS : TT_AGGS_NON_NUMERIC;
                setExtras(extras.map((x, j) => j === i ? { ...x, field: v, agg: vAggs.includes(x.agg) ? x.agg : "count" } : x));
              }}>
                <SelectTrigger data-testid={`tooltip-metric-field-${i}`} className="bg-[color:var(--surface)] flex-1 min-w-0 h-7 text-xs"><SelectValue placeholder="Field" /></SelectTrigger>
                <SelectContent>{fields.map((c) => <SelectItem key={c.name} value={c.name}>{disp ? disp(c.name) : c.name}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={aggs.includes(t.agg) ? t.agg : "count"} onValueChange={(v) => setExtras(extras.map((x, j) => j === i ? { ...x, agg: v } : x))}>
                <SelectTrigger data-testid={`tooltip-metric-agg-${i}`} className="bg-[color:var(--surface)] w-[92px] shrink-0 h-7 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{aggs.map((a) => <SelectItem key={a} value={a}>{a.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
              <button data-testid={`tooltip-metric-up-${i}`} title="Move up" disabled={i === 0}
                onClick={() => { const a = [...extras]; [a[i - 1], a[i]] = [a[i], a[i - 1]]; setExtras(a); }}
                className="text-[color:var(--ink-3)] hover:text-[color:var(--ink)] disabled:opacity-30 p-0.5 shrink-0"><ChevronUp className="w-3 h-3" /></button>
              <button data-testid={`tooltip-metric-down-${i}`} title="Move down" disabled={i === extras.length - 1}
                onClick={() => { const a = [...extras]; [a[i], a[i + 1]] = [a[i + 1], a[i]]; setExtras(a); }}
                className="text-[color:var(--ink-3)] hover:text-[color:var(--ink)] disabled:opacity-30 p-0.5 shrink-0"><ChevronDown className="w-3 h-3" /></button>
              <button onClick={() => setExtras(extras.filter((_, j) => j !== i))} className="text-[color:var(--ink-3)] hover:text-[color:var(--danger)] p-0.5 shrink-0"><X className="w-3.5 h-3.5" /></button>
            </div>
            <Input data-testid={`tooltip-metric-label-${i}`} value={t.label || ""} placeholder="Custom label (optional)"
              onChange={(e) => setExtras(extras.map((x, j) => j === i ? { ...x, label: e.target.value } : x))}
              className="bg-[color:var(--surface)] h-6 text-[11px]" />
            </div>
            );
          })}
          <p className="text-[10px] text-[color:var(--ink-3)]">Extra metrics are queried with the widget and shown under the hovered point (single-series charts).</p>
        </>
      )}
    </div>
  );
}

// ---------- Item 56: per-measure number formatting ----------
const SCALES = [
  { value: "none", label: "No scaling" },
  { value: "auto", label: "Auto (K / M / B)" },
  { value: "auto_in", label: "Auto Indian (K / L / Cr)" },
  { value: "k", label: "Thousands (K)" },
  { value: "m", label: "Millions (M)" },
  { value: "b", label: "Billions (B)" },
  { value: "lakh", label: "Lakh (L)" },
  { value: "crore", label: "Crore (Cr)" },
];
const CURRENCIES = ["USD", "EUR", "GBP", "INR", "JPY", "CNY", "AUD", "CAD"];

export function MeasureFormatEditor({ config, patchConfig }) {
  const measures = config.measures || [];
  const [idx, setIdx] = React.useState(0);
  if (!measures.length) return null;
  const i = Math.min(idx, measures.length - 1);
  const m = measures[i];
  const fmt = m.format || {};
  const setFmt = (patch) => {
    const arr = [...measures];
    arr[i] = { ...m, format: { ...fmt, ...patch } };
    patchConfig({ measures: arr });
  };
  const labelOf = (mm) => mm.key || mm.alias || `${mm.agg || "sum"} of ${mm.field}`;
  return (
    <div data-testid="measure-format-editor" className="space-y-1.5 rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-3">
      <Label className="text-xs text-[color:var(--ink-2)] flex items-center gap-1.5"><Hash className="w-3.5 h-3.5" />Number format</Label>
      {measures.length > 1 && (
        <Select value={String(i)} onValueChange={(v) => setIdx(Number(v))}>
          <SelectTrigger data-testid="mf-measure" className="bg-[color:var(--surface)] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{measures.map((mm, j) => <SelectItem key={j} value={String(j)}>{labelOf(mm)}</SelectItem>)}</SelectContent>
        </Select>
      )}
      <div className="grid grid-cols-2 gap-1.5">
        <Select value={fmt.type || "number"} onValueChange={(v) => setFmt({ type: v, ...(["currency", "accounting"].includes(v) && !fmt.currency ? { currency: "USD" } : {}) })}>
          <SelectTrigger data-testid="mf-type" className="bg-[color:var(--surface)] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="number">Number</SelectItem>
            <SelectItem value="currency">Currency</SelectItem>
            <SelectItem value="percent">Percentage</SelectItem>
            <SelectItem value="accounting">Accounting</SelectItem>
            <SelectItem value="scientific">Scientific</SelectItem>
            <SelectItem value="fraction">Fraction</SelectItem>
            <SelectItem value="duration">Duration (h:mm:ss)</SelectItem>
          </SelectContent>
        </Select>
        <Input data-testid="mf-decimals" type="number" min={0} max={6} value={fmt.decimals ?? 0} onChange={(e) => setFmt({ decimals: Math.max(0, Math.min(6, Number(e.target.value) || 0)) })} className="bg-[color:var(--surface)] h-7 text-xs" placeholder="Decimals" />
      </div>
      {["currency", "accounting"].includes(fmt.type) && (
        <Select value={fmt.currency || "USD"} onValueChange={(v) => setFmt({ currency: v })}>
          <SelectTrigger data-testid="mf-currency" className="bg-[color:var(--surface)] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      )}
      {!["accounting", "duration"].includes(fmt.type || "number") && (
        <Select value={fmt.negStyle || "minus"} onValueChange={(v) => setFmt({ negStyle: v === "minus" ? "" : v })}>
          <SelectTrigger data-testid="mf-negstyle" className="bg-[color:var(--surface)] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="minus">Negatives: -1,234</SelectItem>
            <SelectItem value="paren">Negatives: (1,234)</SelectItem>
          </SelectContent>
        </Select>
      )}
      <Select value={fmt.scale || "none"} onValueChange={(v) => setFmt({ scale: v === "none" ? "" : v })}>
        <SelectTrigger data-testid="mf-scale" className="bg-[color:var(--surface)] h-7 text-xs"><SelectValue /></SelectTrigger>
        <SelectContent>{SCALES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
      </Select>
      <div className="grid grid-cols-2 gap-1.5">
        <Input data-testid="mf-prefix" value={fmt.prefix || ""} onChange={(e) => setFmt({ prefix: e.target.value })} placeholder="Prefix" className="bg-[color:var(--surface)] h-7 text-xs" />
        <Input data-testid="mf-suffix" value={fmt.suffix || ""} onChange={(e) => setFmt({ suffix: e.target.value })} placeholder="Suffix" className="bg-[color:var(--surface)] h-7 text-xs" />
      </div>
    </div>
  );
}

// ---------- Show empty values as ----------
export function EmptyAsSelect({ opts, patchOpts }) {
  return (
    <Row label="Show empty values as">
      <Select value={opts.emptyAs || "dash"} onValueChange={(v) => patchOpts({ emptyAs: v })}>
        <SelectTrigger data-testid="empty-as" className="bg-[color:var(--surface)] w-[100px] h-7 text-xs"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="dash">— (dash)</SelectItem>
          <SelectItem value="zero">0</SelectItem>
          <SelectItem value="blank">Blank</SelectItem>
        </SelectContent>
      </Select>
    </Row>
  );
}
